import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate, formatErr, resolveImage } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MapPin, Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = ["Pending", "Out For Delivery", "Delivered", "Partially Delivered", "Cancelled", "Unable To Deliver"];
const STATUS_COLOR = {
  "Pending": "bg-slate-100 text-slate-700",
  "Out For Delivery": "bg-amber-50 text-amber-700",
  "Delivered": "bg-emerald-50 text-emerald-700",
  "Partially Delivered": "bg-cobalt-50 text-cobalt-700",
  "Cancelled": "bg-rose-50 text-rose-700",
  "Unable To Deliver": "bg-rose-50 text-rose-700",
};

export default function DriverDeliveryDetail() {
  const { did } = useParams();
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  function load() {
    api.get(`/deliveries/${did}`).then(r => {
      setD(r.data);
      setStatus(r.data.status);
      setNotes(r.data.delivery_notes || "");
    });
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [did]);

  async function update() {
    setSaving(true);
    try {
      let gps_lat, gps_lng;
      if (status === "Delivered" && navigator.geolocation) {
        try {
          const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 }));
          gps_lat = pos.coords.latitude;
          gps_lng = pos.coords.longitude;
        } catch { /* ignore */ }
      }
      await api.put(`/deliveries/${did}/status`, { status, delivery_notes: notes, gps_lat, gps_lng });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  if (!d) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div data-testid="driver-delivery-detail">
      <PageHeader
        eyebrow={d.invoice_no}
        title="Delivery Details"
        description={`Delivery Date: ${fmtDate(d.delivery_date)}`}
        actions={<Link to="/driver/deliveries"><Button variant="outline"><ArrowLeft size={14} className="mr-1"/>Back</Button></Link>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLOR[d.status]}`}>{d.status}</span>
            {d.route_name && <span className="text-[11px] uppercase tracking-[0.18em] text-cobalt-600 font-semibold">{d.route_name}</span>}
          </div>
          <div className="font-heading text-xl font-semibold">{d.customer_name}</div>
          {d.customer_address && (
            <div className="mt-3 flex items-start gap-2 text-sm text-slate-600">
              <MapPin size={15} className="mt-0.5 shrink-0 text-cobalt-600"/>
              <a className="hover:underline" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.customer_address)}`}>{d.customer_address}</a>
            </div>
          )}
          {d.customer_mobile && (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
              <Phone size={15} className="text-emerald-600"/>
              <a className="font-mono hover:underline" href={`tel:${d.customer_mobile}`}>{d.customer_mobile}</a>
            </div>
          )}
          {d.delivered_at && (
            <div className="mt-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2">
              <CheckCircle2 size={14}/>Delivered at {new Date(d.delivered_at).toLocaleString()}
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Items to Deliver</h3>
          <div className="space-y-2">
            {(d.items || []).map((it, i) => (
              <div key={i} className="flex items-center gap-3 border border-slate-200 rounded-md p-3" data-testid={`driver-item-${i}`}>
                {it.image_url ? (
                  <img src={resolveImage(it.image_url)} alt="" className="w-14 h-14 rounded object-cover border border-slate-200"/>
                ) : (
                  <div className="w-14 h-14 rounded border border-dashed border-slate-300 bg-slate-50"/>
                )}
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">{it.product_name}</div>
                  <div className="text-sm text-slate-500">{Number(it.quantity)} {it.unit}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-3">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Update Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Delivery Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="driver-status-select"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g. Left at reception, called manager, etc."/>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={update} disabled={saving} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="driver-status-save">
              {saving ? "Saving…" : "Save Update"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
